import { Grid } from '@mui/material';
import { ChangeEvent, ReactNode, useContext, useMemo } from 'react';
import { useOutletContext, useFetcher } from 'react-router';
import { useBackButton } from 'hooks/useBackButton';
import { PhoneType } from 'types';
import AuturaTextField from 'components/inputs/AuturaTextField';
import { useTranslation } from 'react-i18next';
import { ContactContext, getHandlerForDisplayMode } from './ContactsPanel';
import { AppContext } from 'context/appContext';
import AuturaPhoneField from 'components/inputs/AuturaPhoneField';
import AuturaStateField from 'components/inputs/AuturaStateField';
import FormFrame from 'components/layout/FormFrame';
import DetailsHeader from 'components/layout/DetailsHeader';
import SettingsSection from 'components/layout/SettingsSection';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { ScrollableFormContainer } from 'components/layout/ScrollableContainer';
import { PeopleTabActionHandler } from './PeopleTabLayout';
import { getActionRouteForDisplayMode } from './PeopleTabLayout';

export const ContactForm = (): ReactNode => {
  const { t } = useTranslation();
  const { state } = useContext(AppContext);
  const {
    entity,
    contact,
    setContact,
    onBack,
    formMode,
    displayMode,
    fetcherKey,
    contacts,
  } = useOutletContext<ContactContext>();
  const fetcher = useFetcher({ key: fetcherKey });

  const canEdit = state.entityPermissions.contact.update;
  const canCreate = state.entityPermissions.contact.create;

  const readonly = useMemo(() => {
    if (formMode === 'edit') {
      return !canEdit;
    } else if (formMode === 'create') {
      return !canCreate;
    }
    return false;
  }, [canEdit, canCreate, formMode]);

  // handle the browser back button
  useBackButton(onBack);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    // Only used for AuturaTextFields
    const { name, value } = e.target;
    const prevContact = { ...contact };
    switch (name) {
      case 'contact.name':
        setContact({ ...prevContact, name: value });
        break;
      case 'contact.description':
        setContact({ ...prevContact, description: value });
        break;
      case 'contact.website':
        setContact({ ...prevContact, website: value });
        break;
      case 'contact.primaryEmail':
        setContact({ ...prevContact, primaryEmail: value });
        break;
      case 'contact.secondaryEmail':
        setContact({ ...prevContact, secondaryEmail: value });
        break;
      case 'contact.location.street':
        setContact({
          ...prevContact,
          location: {
            ...prevContact.location,
            street: value,
            city: prevContact?.location?.city || '',
            stateCode: prevContact?.location?.stateCode || '',
            postalCode: prevContact?.location?.postalCode || '',
            country: prevContact?.location?.country || '',
          },
        });
        break;
      case 'contact.location.unit':
        setContact({
          ...prevContact,
          location: {
            ...prevContact.location,
            unit: value,
            street: prevContact?.location?.street || '',
            city: prevContact?.location?.city || '',
            stateCode: prevContact?.location?.stateCode || '',
            postalCode: prevContact?.location?.postalCode || '',
            country: prevContact?.location?.country || '',
          },
        });
        break;
      case 'contact.location.city':
        setContact({
          ...prevContact,
          location: {
            ...prevContact.location,
            city: value,
            street: prevContact?.location?.street || '',
            stateCode: prevContact?.location?.stateCode || '',
            postalCode: prevContact?.location?.postalCode || '',
            country: prevContact?.location?.country || '',
          },
        });
        break;
      case 'contact.location.postalCode':
        setContact({
          ...prevContact,
          location: {
            ...prevContact.location,
            postalCode: value,
            street: prevContact?.location?.street || '',
            city: prevContact?.location?.city || '',
            stateCode: prevContact?.location?.stateCode || '',
            country: prevContact?.location?.country || '',
          },
        });
        break;
    }
  };

  const handleSubmit = () => {
    if (formMode === 'edit') {
      updateContact();
    } else if (formMode === 'create' && displayMode !== 'tenant') {
      upsertContact();
    } else if (formMode === 'create' && displayMode === 'tenant') {
      createContact();
    }
  };
  // property/group upsert
  const upsertContact = () => {
    const contactArr = [...contacts, contact];
    fetcher.submit(
      {
        handler: getHandlerForDisplayMode(displayMode, formMode),
        body: JSON.stringify(contactArr),
        tenantID: state.tenant.id,
        entityID: entity.id,
        contactID: 0,
      },
      {
        method: 'PUT',
        action: getActionRouteForDisplayMode({
          displayMode,
          propertyID: entity?.id,
          groupID: entity?.id,
          tenantID: state.tenant.id,
        }),
        encType: 'application/json',
      },
    );
  };
  // tenant create
  const createContact = () => {
    fetcher.submit(
      {
        handler: PeopleTabActionHandler.TENANT_CONTACT_CREATE,
        body: JSON.stringify(contact),
        tenantID: state.tenant.id,
        entityID: entity.id,
        contactID: 0,
      },
      {
        method: 'POST',
        action: getActionRouteForDisplayMode({
          displayMode,
          propertyID: entity?.id,
          groupID: entity?.id,
          tenantID: state.tenant.id,
        }),
        encType: 'application/json',
      },
    );
  };
  // property/group/tenant update
  const updateContact = () => {
    fetcher.submit(
      {
        handler: getHandlerForDisplayMode(displayMode, formMode),
        body: JSON.stringify(contact),
        tenantID: state.tenant.id,
        entityID: entity.id,
        contactID: contact.id,
      },
      {
        method: 'PUT',
        action: getActionRouteForDisplayMode({
          displayMode,
          propertyID: entity?.id,
          groupID: entity?.id,
          tenantID: state.tenant.id,
        }),
        encType: 'application/json',
      },
    );
  };
  const submitButtonText = useMemo(() => {
    switch (formMode) {
      case 'edit':
        return t('buttons.update', {
          name: t('contact.label', { ns: 'entities' }),
        });
      case 'create':
        return t('buttons.create', {
          name: t('contact.label', { ns: 'entities' }),
        });
      default:
        return '';
    }
  }, [formMode]);

  return (
    <ValidatorForm onSubmit={handleSubmit}>
      <FormFrame loading={fetcher.state !== 'idle'}>
        <DetailsHeader
          title={
            contact.name ||
            t('buttons.create', {
              name: t('contact.label', { ns: 'entities' }),
            })
          }
          onCancel={onBack}
          submitLabel={submitButtonText}
          readonly={readonly}
          disableSubmit={fetcher.state !== 'idle'}
        />
        <ScrollableFormContainer maxHeight='calc(100dvh - 326px)'>
          <SettingsSection title={t('details')}>
            <Grid container width={'100%'} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaTextField
                  label={t('contact.name', { ns: 'entities' })}
                  name='contact.name'
                  value={contact.name}
                  onChange={handleChange}
                  required
                  readonly={readonly}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('contact.description', { ns: 'entities' })}
                  name='contact.description'
                  onChange={handleChange}
                  value={contact?.description || ''}
                  readonly={readonly}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('contact.primaryEmail', { ns: 'entities' })}
                  name={'contact.primaryEmail'}
                  value={contact?.primaryEmail || ''}
                  validators={['isEmail']}
                  errorMessages={['Invalid email']}
                  onChange={handleChange}
                  required
                  readonly={readonly}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('contact.secondaryEmail', { ns: 'entities' })}
                  onChange={handleChange}
                  name={'contact.secondaryEmail'}
                  value={contact?.secondaryEmail || ''}
                  readonly={readonly}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaPhoneField
                  label={t(`contact.primaryPhoneMobile`, { ns: 'entities' })}
                  name={'contact.primaryPhone'}
                  typeFieldName={'contact.primaryPhoneType'}
                  typeFieldLabel='Type'
                  phoneNumber={contact?.primaryPhone || ''}
                  setPhoneNumber={(phone: string) => {
                    setContact({ ...contact, primaryPhone: phone });
                  }}
                  phoneType={contact?.primaryPhoneType || PhoneType.None}
                  setPhoneType={(phoneType: PhoneType) => {
                    if (setContact) {
                      setContact({ ...contact, primaryPhoneType: phoneType });
                    }
                  }}
                  readonly={readonly}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaPhoneField
                  label={t(`contact.secondaryPhoneMobile`, { ns: 'entities' })}
                  name={'contact.secondaryPhone'}
                  typeFieldName={'contact.secondaryPhoneType'}
                  typeFieldLabel='Type'
                  phoneNumber={contact?.secondaryPhone || ''}
                  setPhoneNumber={(phone: string) => {
                    setContact({ ...contact, secondaryPhone: phone });
                  }}
                  phoneType={contact?.secondaryPhoneType || PhoneType.None}
                  setPhoneType={(phoneType: PhoneType) => {
                    setContact({ ...contact, secondaryPhoneType: phoneType });
                  }}
                  readonly={readonly}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaPhoneField
                  label={t(`contact.tertiaryPhoneMobile`, { ns: 'entities' })}
                  name={'contact.tertiaryPhone'}
                  typeFieldName={'contact.tertiaryPhoneType'}
                  typeFieldLabel={t('contact.tertiaryPhoneType')}
                  phoneNumber={contact?.tertiaryPhone || ''}
                  setPhoneNumber={(phone: string) => {
                    if (setContact) {
                      setContact({ ...contact, tertiaryPhone: phone });
                    }
                  }}
                  phoneType={contact?.tertiaryPhoneType || PhoneType.None}
                  setPhoneType={(phoneType: PhoneType) => {
                    if (setContact) {
                      setContact({ ...contact, tertiaryPhoneType: phoneType });
                    }
                  }}
                  readonly={readonly}
                />
              </Grid>
            </Grid>
          </SettingsSection>
          <SettingsSection title={t('address')} divider={false}>
            <Grid container width={'100%'} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaTextField
                  label={t('contact.location.street', { ns: 'entities' })}
                  name='contact.location.street'
                  value={contact?.location?.street || ''}
                  onChange={handleChange}
                  readonly={readonly}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('contact.location.unit', { ns: 'entities' })}
                  name='contact.location.unit'
                  value={contact?.location?.unit || ''}
                  onChange={handleChange}
                  readonly={readonly}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('contact.location.city', { ns: 'entities' })}
                  name='contact.location.city'
                  value={contact?.location?.city || ''}
                  onChange={handleChange}
                  readonly={readonly}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  lg: 3,
                }}
              >
                <AuturaStateField
                  label={t('contact.location.stateCode', { ns: 'entities' })}
                  stateCode={contact.location.stateCode || ''}
                  setStateCode={(stateCode: string) => {
                    setContact({
                      ...contact,
                      location: {
                        ...contact.location,
                        stateCode,
                      },
                    });
                  }}
                  readonly={readonly}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('contact.location.postalCode', { ns: 'entities' })}
                  name='contact.location.postalCode'
                  value={contact?.location?.postalCode || ''}
                  onChange={handleChange}
                  readonly={readonly}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('contact.website', { ns: 'entities' })}
                  name={'contact.website'}
                  value={contact?.website || ''}
                  readonly={readonly}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          </SettingsSection>
        </ScrollableFormContainer>
      </FormFrame>
    </ValidatorForm>
  );
};
